import { useMemo, useState } from 'react';
import Papa from 'papaparse';
import { FiDownload, FiFileText, FiHome, FiSliders } from 'react-icons/fi';
import { FaProjectDiagram } from 'react-icons/fa';
import type { Box, BoxDraft } from '../../types';
import { STATUS_OPTIONS } from '../../lib/boxes';
import { Button, Select, TypeBadge } from '../ui';
import { cn } from '../../lib/cn';
import BoxModal from '../BoxModal';

interface ReportsPageProps {
  boxes: Box[];
  uniquePops: string[];
  popNames: string[];
  isAdmin: boolean;
  onSaveBox: (payload: BoxDraft) => Promise<void>;
  onDeleteBox: (id: string) => Promise<void>;
}

export default function ReportsPage({ boxes, uniquePops, popNames, isAdmin, onSaveBox, onDeleteBox }: ReportsPageProps) {
  const [reportPop, setReportPop] = useState('');
  const [reportStatuses, setReportStatuses] = useState<string[]>([]);
  const [reportEditors, setReportEditors] = useState<string[]>([]);
  const [reportDateFrom, setReportDateFrom] = useState('');
  const [reportDateTo, setReportDateTo] = useState('');
  const [reportNameSearch, setReportNameSearch] = useState('');
  const [reportSelectedBox, setReportSelectedBox] = useState<BoxDraft | null>(null);
  const [reportFiltersOpen, setReportFiltersOpen] = useState(true);

  const uniqueEditors = useMemo(() => {
    const vals = new Set(boxes.map((b) => String(b.LastEditor || '').trim()).filter(Boolean));
    return Array.from(vals).sort((a, b) => a.localeCompare(b));
  }, [boxes]);

  const reportBoxes = useMemo(() => {
    const search = reportNameSearch.trim().toLowerCase();
    return boxes.filter((b) => {
      const popOk = !reportPop || (b.pop || '').trim() === reportPop;
      const statusOk = reportStatuses.length === 0 || reportStatuses.includes(String(b.status || ''));
      const editorOk = reportEditors.length === 0 || reportEditors.includes(String(b.LastEditor || '').trim());
      const date = b.LastEditDate ? new Date(b.LastEditDate) : null;
      const fromOk = !reportDateFrom || (date && date >= new Date(reportDateFrom + 'T00:00:00'));
      const toOk = !reportDateTo || (date && date <= new Date(reportDateTo + 'T23:59:59'));
      const nameOk =
        !search ||
        String(b.name || '').toLowerCase().includes(search) ||
        String(b.id || '').toLowerCase().includes(search);
      return popOk && statusOk && editorOk && fromOk && toOk && nameOk;
    });
  }, [boxes, reportPop, reportStatuses, reportEditors, reportDateFrom, reportDateTo, reportNameSearch]);

  const exportReportCsv = () => {
    const csv = Papa.unparse(
      reportBoxes.map((b) => ({
        id: b.id,
        name: b.name,
        pop: b.pop || '',
        type: b.type,
        splitter: b.splitter || '',
        signal: b.signal || '',
        status: b.status || '',
        montada_por: b.montadoPor || '',
        latitude: b.latitude,
        longitude: b.longitude,
        ultimo_editor: b.LastEditor || '',
        data_ultima_edicao: b.LastEditDate ? new Date(b.LastEditDate).toLocaleString('pt-BR') : ''
      }))
    );
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `relatorio_caixas_${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const activeFilters = [
    reportPop && 'POP',
    reportStatuses.length > 0 && 'Status',
    reportEditors.length > 0 && 'Editor',
    reportNameSearch && 'Nome',
    (reportDateFrom || reportDateTo) && 'Data'
  ].filter(Boolean);

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden p-3 sm:p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 text-lg font-bold text-slate-800">
          <FiFileText size={20} />
          Relatórios
        </h3>
        <div className="flex flex-wrap items-center gap-2">
          {!reportFiltersOpen && activeFilters.length > 0 && (
            <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700">
              {activeFilters.join(', ')} ativos
            </span>
          )}
          <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
            {reportBoxes.length} caixas
          </span>
          <Button variant="ghost" size="sm" onClick={() => setReportFiltersOpen((v) => !v)}>
            <FiSliders size={14} />
            <span>{reportFiltersOpen ? 'Ocultar filtros' : 'Filtros'}</span>
          </Button>
          <Button variant="success" size="sm" onClick={exportReportCsv}>
            <FiDownload size={16} />
            <span>Exportar CSV</span>
          </Button>
        </div>
      </div>

      {reportFiltersOpen && (
        <div className="flex flex-wrap items-start gap-4 rounded-xl border border-slate-200 bg-white p-3">
          <div className="flex items-center gap-2">
            <FiHome size={16} className="text-slate-500" />
            <Select value={reportPop} onChange={(e) => setReportPop(e.target.value)} className="w-44">
              {uniquePops.map((pop) => (
                <option key={pop} value={pop}>
                  {pop === '' ? 'Todos os POPs' : pop}
                </option>
              ))}
            </Select>
          </div>

          <div className="flex flex-col gap-1">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Status</span>
            <div className="flex flex-wrap items-center gap-1.5">
              {STATUS_OPTIONS.filter(Boolean).map((s) => (
                <button
                  key={s}
                  className={cn(
                    'rounded-full border px-3 py-1 text-xs font-medium transition',
                    reportStatuses.includes(s)
                      ? 'border-blue-500 bg-blue-600 text-white'
                      : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                  )}
                  onClick={() =>
                    setReportStatuses((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]))
                  }
                >
                  {s}
                </button>
              ))}
              {reportStatuses.length > 0 && (
                <button className="text-xs font-medium text-blue-600 hover:underline" onClick={() => setReportStatuses([])}>
                  limpar
                </button>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-1">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Editor</span>
            <div className="flex flex-wrap items-center gap-1.5">
              {uniqueEditors.map((e) => (
                <button
                  key={e}
                  className={cn(
                    'rounded-full border px-3 py-1 text-xs font-medium transition',
                    reportEditors.includes(e)
                      ? 'border-blue-500 bg-blue-600 text-white'
                      : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                  )}
                  onClick={() =>
                    setReportEditors((prev) => (prev.includes(e) ? prev.filter((x) => x !== e) : [...prev, e]))
                  }
                >
                  {e}
                </button>
              ))}
              {reportEditors.length > 0 && (
                <button className="text-xs font-medium text-blue-600 hover:underline" onClick={() => setReportEditors([])}>
                  limpar
                </button>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-1">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Buscar caixa</span>
            <div className="relative flex items-center">
              <FaProjectDiagram size={14} className="pointer-events-none absolute left-3 text-slate-400" />
              <input
                type="text"
                className="h-9 w-56 rounded-lg border border-slate-200 bg-white pl-9 pr-8 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                placeholder="Nome ou ID da caixa..."
                value={reportNameSearch}
                onChange={(e) => setReportNameSearch(e.target.value)}
              />
              {reportNameSearch && (
                <button className="absolute right-2 text-slate-400 hover:text-slate-600" onClick={() => setReportNameSearch('')}>
                  ✕
                </button>
              )}
            </div>
          </div>

          <div className="flex items-end gap-2">
            <label className="flex flex-col text-[11px] font-semibold uppercase tracking-wide text-slate-400">
              De
              <input
                type="date"
                className="mt-0.5 rounded-md border border-slate-200 px-2 py-1.5 text-xs text-slate-700 outline-none focus:border-blue-500"
                value={reportDateFrom}
                onChange={(e) => setReportDateFrom(e.target.value)}
              />
            </label>
            <label className="flex flex-col text-[11px] font-semibold uppercase tracking-wide text-slate-400">
              Até
              <input
                type="date"
                className="mt-0.5 rounded-md border border-slate-200 px-2 py-1.5 text-xs text-slate-700 outline-none focus:border-blue-500"
                value={reportDateTo}
                onChange={(e) => setReportDateTo(e.target.value)}
              />
            </label>
            {(reportDateFrom || reportDateTo) && (
              <button
                className="pb-2 text-xs font-medium text-blue-600 hover:underline"
                onClick={() => {
                  setReportDateFrom('');
                  setReportDateTo('');
                }}
              >
                limpar
              </button>
            )}
          </div>
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full border-collapse text-sm">
          <thead className="sticky top-0 z-10 bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3">ID</th>
              <th className="px-4 py-3">Nome</th>
              <th className="px-4 py-3">POP</th>
              <th className="px-4 py-3">Tipo</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Montada por</th>
              <th className="px-4 py-3">Sinal</th>
              <th className="px-4 py-3">Último editor</th>
              <th className="px-4 py-3">Data da edição</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {reportBoxes.slice(0, 500).map((b) => (
              <tr
                key={b.id}
                className="cursor-pointer hover:bg-slate-50"
                onClick={() => setReportSelectedBox({ ...b, originalId: b.id })}
              >
                <td className="px-4 py-2.5">
                  <span className="rounded-md bg-slate-100 px-2 py-0.5 font-mono text-xs text-slate-600">{b.id}</span>
                </td>
                <td className="px-4 py-2.5 text-slate-700">{b.name}</td>
                <td className="px-4 py-2.5 text-slate-600">{b.pop || '-'}</td>
                <td className="px-4 py-2.5">
                  <TypeBadge type={b.type} />
                </td>
                <td className="px-4 py-2.5">
                  <span
                    className={cn(
                      'rounded-full px-2 py-0.5 text-xs font-medium',
                      b.status ? 'bg-emerald-100 text-emerald-700' : 'text-slate-400'
                    )}
                  >
                    {b.status || '-'}
                  </span>
                </td>
                <td className="px-4 py-2.5 text-slate-600">{b.montadoPor || '-'}</td>
                <td className="px-4 py-2.5 text-slate-600">{b.signal || '-'}</td>
                <td className="px-4 py-2.5 text-slate-600">{b.LastEditor || '-'}</td>
                <td className="px-4 py-2.5 text-slate-600">
                  {b.LastEditDate ? new Date(b.LastEditDate).toLocaleString('pt-BR') : '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {reportSelectedBox && (
        <BoxModal
          box={reportSelectedBox}
          isAdmin={isAdmin}
          popOptions={popNames}
          onClose={() => setReportSelectedBox(null)}
          onSave={(payload) =>
            onSaveBox(payload)
              .then(() => setReportSelectedBox(null))
              .catch((err) => window.alert((err as Error).message || 'Erro ao salvar'))
          }
          onDelete={(id) =>
            onDeleteBox(id)
              .then(() => setReportSelectedBox(null))
              .catch((err) => window.alert((err as Error).message || 'Erro ao excluir'))
          }
        />
      )}
    </div>
  );
}
